/**
 * The words the renderer and the core's screens learned closing the remaining items of #4, #5, #7,
 * #26, #27, #29, #31 and #76: a bulk action that asks its fields once, an answer that is an address
 * to go to, an empty list waiting on a catalogue, the data-start setting, and the sections extra
 * fields are grouped in. Kept beside the catalogue and spread into it, so a parallel change to the
 * catalogue does not collide with these.
 */
export const CORE_SCREENS_EN = {
    'list.bulk.form.description': 'Answered once, for every row. The server judges each row on its own: a row that breaks a rule is refused and the reason is shown; the rest go through.',
    'list.bulk.form.first': 'Answer the questions above first; the server is then asked what each row would come to.',
    'action.link.none': 'The answer carried no address to open.',
    'screen.media.files.action.open': 'Download',
    'extensions.admin.field.section': 'Section',
    'extensions.admin.field.section.none': 'None — with the other extra fields',
    'extensions.admin.col.section': 'Section',
    'extensions.admin.col.fields': 'Fields',
    'extensions.admin.sections': 'Sections',
    'extensions.admin.sections.help': 'Titled groups this entity’s extra fields sit in. Each is a heading of its own on the form and a card of its own on the record page.',
    'extensions.admin.sections.empty': 'No sections yet',
    'extensions.admin.sections.empty.description': 'Every extra field is drawn under one heading until a section is added and fields are put in it.',
    'extensions.admin.section.add': 'Add a section',
    'extensions.admin.section.key.help': 'Lower-case letters, digits and underscores. Fixed once saved: fields name the section by it.',
    'extensions.admin.section.saved': 'The section was saved.',
    'extensions.admin.section.retired': 'The section was retired. Its fields are drawn with the other extra fields.',
    'extensions.admin.section.retire.title': 'Retire the section «{section}»?',
    'extensions.admin.section.retire.description': 'Its heading goes from every form and record page, and its fields are drawn with the other extra fields. Nothing typed into them is lost.',
    'extensions.refusal.section_shape': 'A section is named by its key: lower-case letters, digits and underscores.',
    'extensions.refusal.section_unknown': '«{section}» is not a section of this entity.',
    'extensions.refusal.section_label': 'A section needs its name in both Arabic and English.',
    'extensions.refusal.section_label_long': 'A section’s name is at most {max} characters.',
    'extensions.refusal.no_section': 'There is no such section.',
    'extensions.refusal.too_many_sections': 'An entity takes at most {max} sections.',
    'change.field.field.section': 'Section',
    'change.field.field_section.entity': 'Entity',
    'change.field.field_section.key': 'Key',
    'change.field.field_section.position': 'Position',
    'change.field.field_section.label_ar': 'Name (Arabic)',
    'change.field.field_section.label_en': 'Name (English)',
    'change.field.field_section.status': 'Status',
    'change.field.field_section.retired_at': 'Retired at',
    'change.field.field_section.retired_by': 'Retired by',
    'change.field.field_section.created_by': 'Added by',
    'parties.address.one_billing': 'This party already has a billing address ({address}). Change that one, or retire it first.',
    'screen.calendar.settings.column.records_from': 'Records held from',
    'screen.calendar.settings.field.records_from': 'Records held from',
    'screen.calendar.settings.field.records_from.help': 'The day this workspace went live: every exported file says its data begins then. Leave it empty until it is known.',
    'platform.refusal.calendar.records_from_future': 'The day records begin has to be a day that has come.',
    'change.field.calendar_settings.records_from': 'Records held from',
    'screen.calendar.settings.column.weekend_days': 'Weekend',
    'screen.calendar.settings.action.edit_workweek': 'Working week',
    'screen.calendar.settings.column.work_starts': 'Work starts',
    'screen.calendar.settings.column.work_ends': 'Work ends',
    'screen.calendar.settings.field.weekend_days': 'Weekend',
    'screen.calendar.settings.field.work_starts': 'Work starts',
    'screen.calendar.settings.field.work_ends': 'Work ends',
    'screen.calendar.settings.weekday.0': 'Sunday',
    'screen.calendar.settings.weekday.1': 'Monday',
    'screen.calendar.settings.weekday.2': 'Tuesday',
    'screen.calendar.settings.weekday.3': 'Wednesday',
    'screen.calendar.settings.weekday.4': 'Thursday',
    'screen.calendar.settings.weekday.5': 'Friday',
    'screen.calendar.settings.weekday.6': 'Saturday',
    'change.field.calendar_settings.weekend_days': 'Weekend',
    'change.field.calendar_settings.work_starts': 'Work starts',
    'change.field.calendar_settings.work_ends': 'Work ends',
    'platform.refusal.calendar.weekend': 'The weekend is days of the week, Sunday to Saturday.',
    'platform.refusal.calendar.weekend_all': 'At least one day of the week is a working day.',
    'platform.refusal.calendar.work_hours': 'The working day ends after it starts.',
    'platform.refusal.calendar.clock': 'Write a time of day, as 09:00.',
    'platform.refusal.calendar.date': 'Write a day as YYYY-MM-DD.',
    'platform.refusal.calendar.add': 'A whole number of working days, 0 to 365.',
    // What a list says when it has no rows (docs/list-standard.md §11).
    "screen.identity.people.empty": "No one has been added to this workspace yet.",
    "screen.identity.roles.empty": "No roles yet; a role gathers the permissions a job needs.",
    "screen.parties.parties.empty": "No people or companies are registered yet.",
    "screen.privacy.register.empty": "No categories of personal data are recorded yet.",
    "screen.privacy.requests.empty": "No one has asked about their data yet.",
    "screen.privacy.breaches.empty": "No breach has been recorded.",
    "screen.calendar.occasions.empty": "No occasions yet; add the dates this workspace observes.",
    "screen.documents.items.empty": "No documents are filed yet.",
    "screen.documents.types.empty": "No document types yet.",
    "screen.notifications.rules.empty": "No notification rules yet.",
    "screen.notifications.announcements.empty": "No announcements yet.",
    "screen.forms.catalogue.empty": "No forms built yet.",
};
export const CORE_SCREENS_AR = {
    'list.bulk.form.description': 'يُجاب مرّة واحدة لكل الصفوف. ويحكم الخادم على كل صفّ وحده: ما يخالف قاعدةً يُرفض ويُقال لماذا، والباقي يمرّ.',
    'list.bulk.form.first': 'أجب عن الأسئلة أعلاه أولاً، ثم يُسأل الخادم عمّا سيؤول إليه كل صفّ.',
    'action.link.none': 'لم يحمل الجواب عنواناً يُفتح.',
    'screen.media.files.action.open': 'تنزيل',
    'extensions.admin.field.section': 'القسم',
    'extensions.admin.field.section.none': 'بلا قسم — مع باقي الحقول الإضافية',
    'extensions.admin.col.section': 'القسم',
    'extensions.admin.col.fields': 'الحقول',
    'extensions.admin.sections': 'الأقسام',
    'extensions.admin.sections.help': 'مجموعات معنونة تنتظم فيها الحقول الإضافية لهذا الكيان. لكلّ منها عنوانه في النموذج وبطاقته في صفحة السجل.',
    'extensions.admin.sections.empty': 'لا أقسام بعد',
    'extensions.admin.sections.empty.description': 'تُرسم الحقول الإضافية كلها تحت عنوان واحد حتى يُضاف قسم وتوضع فيه حقول.',
    'extensions.admin.section.add': 'أضف قسماً',
    'extensions.admin.section.key.help': 'أحرف لاتينية صغيرة وأرقام وشرطة سفلية. ثابت بعد الحفظ: به تسمّي الحقولُ القسم.',
    'extensions.admin.section.saved': 'حُفظ القسم.',
    'extensions.admin.section.retired': 'أُحيل القسم إلى التقاعد. تُرسم حقوله مع باقي الحقول الإضافية.',
    'extensions.admin.section.retire.title': 'أتُحيل القسم «{section}» إلى التقاعد؟',
    'extensions.admin.section.retire.description': 'يزول عنوانه من كل نموذج وصفحة سجل، وتُرسم حقوله مع باقي الحقول الإضافية. ولا يضيع شيء مما كُتب فيها.',
    'extensions.refusal.section_shape': 'يُسمّى القسم بمفتاحه: أحرف لاتينية صغيرة وأرقام وشرطة سفلية.',
    'extensions.refusal.section_unknown': '«{section}» ليس قسماً من أقسام هذا الكيان.',
    'extensions.refusal.section_label': 'يحتاج القسم إلى اسمه بالعربية والإنجليزية معاً.',
    'extensions.refusal.section_label_long': 'لا يزيد اسم القسم على {max} حرفاً.',
    'extensions.refusal.no_section': 'لا يوجد قسم كهذا.',
    'extensions.refusal.too_many_sections': 'لا يأخذ الكيان أكثر من {max} قسماً.',
    'change.field.field.section': 'القسم',
    'change.field.field_section.entity': 'الكيان',
    'change.field.field_section.key': 'المفتاح',
    'change.field.field_section.position': 'الترتيب',
    'change.field.field_section.label_ar': 'الاسم (بالعربية)',
    'change.field.field_section.label_en': 'الاسم (بالإنجليزية)',
    'change.field.field_section.status': 'الحالة',
    'change.field.field_section.retired_at': 'تقاعد في',
    'change.field.field_section.retired_by': 'أحاله إلى التقاعد',
    'change.field.field_section.created_by': 'أضافه',
    'parties.address.one_billing': 'لهذا الطرف عنوان فوترة من قبل ({address}). عدّله هو، أو أحِله إلى التقاعد أولاً.',
    'screen.calendar.settings.column.records_from': 'السجلات متاحة منذ',
    'screen.calendar.settings.field.records_from': 'السجلات متاحة منذ',
    'screen.calendar.settings.field.records_from.help': 'اليوم الذي بدأ فيه العمل على هذه المساحة: يقول كل ملف مُصدَّر إن بياناته تبدأ منه. اتركه فارغاً حتى يُعرف.',
    'platform.refusal.calendar.records_from_future': 'يوم بدء السجلات يجب أن يكون يوماً قد حلّ.',
    'change.field.calendar_settings.records_from': 'السجلات متاحة منذ',
    'screen.calendar.settings.column.weekend_days': 'عطلة نهاية الأسبوع',
    'screen.calendar.settings.action.edit_workweek': 'أيام العمل',
    'screen.calendar.settings.column.work_starts': 'بداية الدوام',
    'screen.calendar.settings.column.work_ends': 'نهاية الدوام',
    'screen.calendar.settings.field.weekend_days': 'عطلة نهاية الأسبوع',
    'screen.calendar.settings.field.work_starts': 'بداية الدوام',
    'screen.calendar.settings.field.work_ends': 'نهاية الدوام',
    'screen.calendar.settings.weekday.0': 'الأحد',
    'screen.calendar.settings.weekday.1': 'الاثنين',
    'screen.calendar.settings.weekday.2': 'الثلاثاء',
    'screen.calendar.settings.weekday.3': 'الأربعاء',
    'screen.calendar.settings.weekday.4': 'الخميس',
    'screen.calendar.settings.weekday.5': 'الجمعة',
    'screen.calendar.settings.weekday.6': 'السبت',
    'change.field.calendar_settings.weekend_days': 'عطلة نهاية الأسبوع',
    'change.field.calendar_settings.work_starts': 'بداية الدوام',
    'change.field.calendar_settings.work_ends': 'نهاية الدوام',
    'platform.refusal.calendar.weekend': 'العطلة أيام من الأسبوع، من الأحد إلى السبت.',
    'platform.refusal.calendar.weekend_all': 'يجب أن يبقى يوم عمل واحد على الأقل في الأسبوع.',
    'platform.refusal.calendar.work_hours': 'نهاية الدوام بعد بدايته.',
    'platform.refusal.calendar.clock': 'اكتب وقتًا من اليوم، مثل 09:00.',
    'platform.refusal.calendar.date': 'اكتب اليوم بصيغة YYYY-MM-DD.',
    'platform.refusal.calendar.add': 'عدد صحيح من أيام العمل، من 0 إلى 365.',
    // What a list says when it has no rows (docs/list-standard.md §11).
    "screen.identity.people.empty": "لم يُضف أحد إلى هذه المساحة بعد.",
    "screen.identity.roles.empty": "لا أدوار بعد؛ الدور يجمع الصلاحيات التي تحتاجها وظيفة.",
    "screen.parties.parties.empty": "لا أشخاص ولا شركات مسجّلة بعد.",
    "screen.privacy.register.empty": "لم تُسجَّل فئات للبيانات الشخصية بعد.",
    "screen.privacy.requests.empty": "لم يطلب أحد شيئاً بشأن بياناته بعد.",
    "screen.privacy.breaches.empty": "لم يُسجَّل أيّ تسرّب.",
    "screen.calendar.occasions.empty": "لا مناسبات بعد؛ أضف التواريخ التي تعتمدها المساحة.",
    "screen.documents.items.empty": "لم تُحفظ وثائق بعد.",
    "screen.documents.types.empty": "لا أنواع وثائق بعد.",
    "screen.notifications.rules.empty": "لا قواعد إشعار بعد.",
    "screen.notifications.announcements.empty": "لا إعلانات بعد.",
    "screen.forms.catalogue.empty": "لم يُبنَ أيّ نموذج بعد.",
};
//# sourceMappingURL=i18n-core-screens.js.map